import React from 'react';
import { Table, Card, Typography, Tag, Space, Button, Modal, Spin, Avatar } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getAllMessages, getMessage } from './api';
import { MessageOutlined, UserOutlined, BookOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface MessageSession {
  sessionId: number;
  user: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
  question: string;
  answer: string;
  messageCount: number;
  userMessageCount: number;
  aiMessageCount: number;
  estimatedTokens: number;
  realTokens?: {
    input: number;
    output: number;
    total: number;
  } | null;
  startedAt: string;
  lastMessageAt: string;
  messages: Array<{
    id: number;
    sender: string;
    content: string;
    createdAt: string;
  }>;
}

const MessagesTable: React.FC = () => {
  const [selectedSession, setSelectedSession] = React.useState<MessageSession | null>(null);
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  const { data: sessions, isLoading, error } = useQuery({
    queryKey: ['messages'],
    queryFn: getAllMessages,
  });

  const handleViewSession = (session: MessageSession) => {
    setSelectedSession(session);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Пользователь',
      key: 'user',
      width: 200,
      render: (record: MessageSession) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar 
            src={record.user?.avatarUrl} 
            icon={<UserOutlined />} 
            size={40}
          />
          <div>
            <div style={{ fontWeight: '500', fontSize: '13px' }}>
              {record.user?.firstName && record.user?.lastName 
                ? `${record.user.firstName} ${record.user.lastName}`
                : record.user?.email || 'Неизвестный'
              }
            </div>
            {record.user?.email && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                {record.user.email}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Сообщения',
      key: 'messageCount',
      width: 100,
      render: (record: MessageSession) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {record.messageCount}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {record.userMessageCount}/{record.aiMessageCount}
          </div>
        </div>
      ),
    },
    {
      title: 'Токены',
      key: 'estimatedTokens',
      width: 120,
      render: (record: MessageSession) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: record.realTokens ? '#4caf50' : '#1976d2' }}>
            {record.realTokens ? record.realTokens.total.toLocaleString() : record.estimatedTokens.toLocaleString()}
          </div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            {record.realTokens ? 'реальные' : 'примерные'}
          </div>
          {record.realTokens && (
            <div style={{ fontSize: '9px', color: '#999' }}>
              {record.realTokens.input}/{record.realTokens.output}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Дата',
      key: 'lastMessageAt',
      width: 120,
      render: (record: MessageSession) => (
        <div style={{ fontSize: '11px' }}>
          {new Date(record.lastMessageAt).toLocaleString('ru-RU', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (record: MessageSession) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => handleViewSession(record)}
        >
          Просмотр
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        Ошибка загрузки сообщений: {(error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, color: "#1976d2" }}>
          <MessageOutlined style={{ marginRight: 8 }} />
          Обращения к ИИ
        </Title>
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <Table
            dataSource={sessions}
            columns={columns}
            rowKey="sessionId"
            scroll={{ x: 700 }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} из ${total} диалогов`,
            }}
            size="small"
          />
        </div>
      </Card>

      <Modal
        title="Полный диалог"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Закрыть
          </Button>
        ]}
        width={900}
      >
        {selectedSession && (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar 
                src={selectedSession.user?.avatarUrl} 
                icon={<UserOutlined />} 
                size={50}
              />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {selectedSession.user?.firstName && selectedSession.user?.lastName 
                    ? `${selectedSession.user.firstName} ${selectedSession.user.lastName}`
                    : selectedSession.user?.email || 'Неизвестный пользователь'
                  }
                </div>
                {selectedSession.user?.email && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {selectedSession.user.email}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Статистика:</strong>{' '}
              {selectedSession.messageCount} сообщений ({selectedSession.userMessageCount} от пользователя, {selectedSession.aiMessageCount} от ИИ)
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Дата начала:</strong>{' '}
              {new Date(selectedSession.startedAt).toLocaleString('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Полный диалог:</strong>
            </div>
            <div style={{
              background: '#f8f9fa',
              padding: 16,
              borderRadius: 8,
              maxHeight: 500,
              overflow: 'auto',
              border: '1px solid #e9ecef'
            }}>
              {selectedSession.messages.map((message, index) => (
                <div key={message.id} style={{ 
                  marginBottom: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: message.sender === 'user' ? '#e3f2fd' : '#f1f8e9',
                  borderLeft: `4px solid ${message.sender === 'user' ? '#2196f3' : '#4caf50'}`
                }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    marginBottom: 8,
                    color: message.sender === 'user' ? '#1976d2' : '#2e7d32'
                  }}>
                    {message.sender === 'user' ? '👤 Пользователь' : '🤖 ИИ'}
                  </div>
                  <div style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.5
                  }}>
                    {message.content}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: 8,
                    textAlign: 'right'
                  }}>
                    {new Date(message.createdAt).toLocaleString('ru-RU', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MessagesTable;
